import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/data";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/homeowners", label: "Homeowners" },
  { to: "/commercial", label: "Commercial" },
  { to: "/pricing", label: "Pricing" },
  { to: "/emergency", label: "Emergency" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 border-b ${
        scrolled ? "bg-background/85 backdrop-blur-xl border-border shadow-[0_1px_0_0_rgba(0,0,0,0.04)]" : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
        <div className="h-16 md:h-20 flex items-center justify-between gap-6">
          <Link to="/" data-testid="header-logo" className="flex items-baseline gap-2 group">
            <span className="font-display text-xl md:text-2xl font-extrabold tracking-tight text-foreground">
              RSR
            </span>
            <span className="hidden sm:inline overline">Australia</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-6" data-testid="primary-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `nav-link text-sm font-medium ${isActive ? "text-foreground" : "text-foreground/70 hover:text-foreground"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="header-call-cta"
              className="hidden sm:inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white text-xs md:text-sm font-semibold px-4 py-2.5 transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              <span className="tracking-wide">Call {PHONE_DISPLAY}</span>
            </a>
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="header-call-mobile"
              aria-label="Call RSR Australia"
              className="sm:hidden inline-flex items-center justify-center w-10 h-10 bg-accent text-white"
            >
              <Phone className="w-4 h-4" />
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(v => !v)}
              aria-label="Toggle menu"
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-border text-foreground"
            >
              {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-background/98 backdrop-blur-xl border-t border-border" data-testid="mobile-menu">
          <div className="px-5 py-5 flex flex-col gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="py-3 border-b border-border text-foreground/85 hover:text-foreground text-base"
              >
                {n.label}
              </Link>
            ))}
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="mobile-menu-call"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-accent text-white font-semibold py-3"
            >
              <Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
